<template>
  <div class="client-download-hub" :class="'client-download-hub--' + layout">
    <div class="client-download-tabs">
      <button
        v-for="(col, index) in columns"
        :key="col.key"
        type="button"
        class="client-tab"
        :class="{ active: activeIndex === index }"
        @click="selectPlatform(index)"
      >
        <span class="tab-icon-shell" :class="'tab-icon-shell--' + col.key">
          <img
            v-if="resolveDownloadIcon(col)"
            :src="resolveDownloadIcon(col)"
            alt=""
            class="tab-icon"
          />
          <i v-else :class="['tab-fallback-icon', col.fallbackIcon]"></i>
        </span>
        <span class="tab-title">{{ $tt(tabLabel(col)) }}</span>
      </button>
    </div>

    <div class="client-download-body">
      <div class="body-main">
        <p class="body-label">{{ bodyTitle }}</p>

        <div v-if="showArchStep" class="arch-pick-row">
          <span class="arch-pick-label">{{ $tt("选择 CPU 架构") }}</span>
          <div class="arch-pick-btns">
            <button
              v-for="o in archOptions"
              :key="o.value"
              type="button"
              class="arch-chip"
              :class="{ active: selectedArch === o.value }"
              @click="pickArch(o.value)"
            >
              {{ $tt(o.label) }}
            </button>
          </div>
        </div>

        <p v-else class="arch-hint">{{ bodyHint }}</p>
      </div>

      <div class="body-actions">
        <button
          type="button"
          class="body-download-btn"
          :disabled="!canProceed"
          @click="onPrimaryAction"
        >
          <i :class="primaryIcon"></i>
          {{ $tt(primaryButtonText) }}
        </button>
      </div>
    </div>

    <ClientDownloadDialog
      :visible.sync="dialogVisible"
      :client-type="dialogClientType"
      :initial-cpu-arch="dialogCpuArch"
    />
  </div>
</template>

<script>
import { resolveDownloadIcon } from "@/utils/downloadIcons";
import ClientDownloadDialog from "@/components/ClientDownloadDialog.vue";
import {
  CLIENT_DOWNLOAD_ORDER,
  needsArchPicker,
  defaultArch,
  archOptionsForClientType,
} from "@/utils/clientPlatforms";

export default {
  name: "ClientDownloadHub",
  components: { ClientDownloadDialog },
  props: {
    /** login | home */
    layout: { type: String, default: "login" },
    /** 合并 welcome 接口里的图标 */
    welcomeList: { type: Array, default: () => [] },
  },
  data() {
    return {
      activeIndex: 0,
      selectedArch: defaultArch("kylin"),
      archConfirmed: false,
      dialogVisible: false,
      dialogClientType: "android",
      dialogCpuArch: "",
    };
  },
  computed: {
    columns() {
      const byName = {};
      const byId = {};
      (this.welcomeList || []).forEach((item) => {
        const name = item.name || item.title;
        const id = String(item.id || "");
        if (name) byName[name] = item;
        if (id) byId[id] = item;
      });
      return CLIENT_DOWNLOAD_ORDER.map((spec) => {
        const remote = byName[spec.matchName] || byId[spec.welcomeId] || {};
        return {
          ...spec,
          icon: remote.icon || "",
        };
      });
    },
    activeClient() {
      return this.columns[this.activeIndex] || this.columns[0] || {};
    },
    activeKey() {
      return this.activeClient.key || "android";
    },
    archOptions() {
      return archOptionsForClientType(this.activeKey);
    },
    needsArch() {
      return needsArchPicker(this.activeKey);
    },
    showArchStep() {
      return this.needsArch && this.archOptions.length > 0;
    },
    canProceed() {
      if (!this.needsArch) return true;
      return !!this.selectedArch;
    },
    bodyTitle() {
      return this.$tt(this.activeClient.title || "");
    },
    bodyHint() {
      if (this.activeKey === "windows") {
        return this.$tt("Windows 8～11 通用安装包，选择版本后即可下载");
      }
      return this.$tt("选择版本查看更新说明并下载安装包");
    },
    primaryButtonText() {
      if (this.needsArch && !this.selectedArch) {
        return "请先选择架构";
      }
      return "选择版本并下载";
    },
    primaryIcon() {
      return "el-icon-download";
    },
  },
  watch: {
    welcomeList() {
      this.syncArch();
    },
  },
  mounted() {
    this.syncArch();
  },
  methods: {
    resolveDownloadIcon,
    tabLabel(col) {
      if (this.layout === "login" && col.tabShort) {
        return col.tabShort;
      }
      return col.title;
    },
    selectPlatform(index) {
      this.activeIndex = index;
      this.syncArch();
    },
    syncArch() {
      if (this.needsArch) {
        this.selectedArch = defaultArch(this.activeKey);
        this.archConfirmed = true;
      } else {
        this.selectedArch = "";
        this.archConfirmed = true;
      }
    },
    pickArch(value) {
      this.selectedArch = value;
      this.archConfirmed = true;
    },
    onPrimaryAction() {
      if (!this.canProceed) return;
      this.openDownloadDialog();
    },
    openDownloadDialog() {
      this.dialogClientType = this.activeKey;
      this.dialogCpuArch = this.needsArch ? this.selectedArch : "";
      this.dialogVisible = true;
    },
  },
};
</script>

<style scoped lang="scss">
.client-download-hub--login {
  .client-download-tabs {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 12px;
  }
  .client-tab {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-width: 0;
    min-height: 82px;
    height: 100%;
    padding: 10px 6px 8px;
    gap: 8px;
    box-sizing: border-box;
    .tab-icon-shell {
      width: 36px;
      height: 36px;
    }
    .tab-title {
      width: 100%;
      font-size: 12px;
      line-height: 1.35;
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .client-download-body {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 16px 18px;
  }
  .body-main {
    min-width: 0;
  }
  .body-label {
    font-size: 16px;
    margin-bottom: 8px;
  }
  .arch-hint {
    line-height: 1.5;
  }
  .body-actions {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }
  .body-download-btn {
    max-width: 100%;
  }
}

.client-download-hub--home {
  .client-download-tabs {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 12px;
  }
  .client-tab {
    flex-direction: column;
    min-height: 100px;
    padding: 16px 8px;
    background: #f0f2f5;
    border-radius: 12px;
    border: 2px solid transparent;
    &.active {
      background: #fff;
      border-color: #409eff;
    }
    .tab-title {
      margin-top: 8px;
      font-size: 15px;
    }
  }
}

.client-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-sizing: border-box;
  cursor: pointer;
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.85);
  color: #606266;
  transition: border-color 0.2s, box-shadow 0.2s;

  &.active {
    border-color: #409eff;
    color: #409eff;
    box-shadow: 0 4px 14px rgba(64, 158, 255, 0.15);
  }
}

.tab-icon-shell {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  flex-shrink: 0;
  &--android {
    background: #fff7e6;
  }
  &--ios {
    background: #f0f0f0;
  }
  &--windows {
    background: #ecf5ff;
  }
  &--kylin {
    border-radius: 50%;
    background: linear-gradient(145deg, #ecf5ff 0%, #d9ecff 100%);
  }
  &--mac {
    background: #ecf5ff;
  }
}

.tab-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.tab-fallback-icon {
  font-size: 20px;
  color: #409eff;
}

.client-download-body {
  padding: 14px 18px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #ebeef5;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.body-main {
  flex: 1;
  min-width: 200px;
  text-align: left;
}

.body-label {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.arch-pick-label {
  display: block;
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}

.arch-pick-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.arch-chip {
  border: 1px solid #dcdfe6;
  background: #f5f7fa;
  border-radius: 16px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  color: #606266;
  &.active {
    border-color: #409eff;
    background: #ecf5ff;
    color: #409eff;
  }
}

.arch-hint {
  margin: 0;
  font-size: 13px;
  color: #909399;
}

.body-download-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 20px;
  border: none;
  border-radius: 18px;
  font-size: 14px;
  color: #fff;
  background: #409eff;
  cursor: pointer;
  white-space: nowrap;
  &:disabled {
    background: #c0c4cc;
    cursor: not-allowed;
  }
}
</style>
